import { useEffect } from "react";
import { Modal, Typography, TextField, Button, Box, Stack, Paper, Chip } from "@mui/material";
import ModalBox from "../../themes/overrides/ModalBox";
import { useState } from "react";
import { CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const PreviewUrlModal = ({ isOpen, onClose, onSubmit, loading, previewUrlValue }) => {
    const [url, setUrl] = useState(previewUrlValue || "");
    const [error, setError] = useState("");
    const [copied, setCopied] = useState(false);

    const variables = [
        { name: 'entry.id', value: `{entry.id}`, description: 'The unique ID of the content entry' },
        { name: 'contentType.id', value: `{contentType.id}`, description: 'The ID of the content type (blueprint)' },
        { name: 'workspace.id', value: `{workspace.id}`, description: 'The ID of the workspace this content belongs to' },

    ];

    // Reset the URL when the modal opens/closes or previewUrlValue changes
    useEffect(() => {
        setUrl(previewUrlValue || "");
    }, [isOpen, previewUrlValue]);

    const handleSubmit = () => {
        if (!url) {
            setError("Please enter a URL");
            return;
        }

        try {
            new URL(url.replace(/\{.*?\}/g, 'placeholder')); // Temporary replace variables for validation
            onSubmit(url);
            setUrl("");
            onClose();
        } catch (e) {
            setError("Please enter a valid URL (include http:// or https://)");
        }
    };

    const handleChange = (e) => {
        setUrl(e.target.value);
        if (error) setError("");
    };

    const addVariable = (variable) => {
        const textarea = document.querySelector('textarea');
        const startPos = textarea.selectionStart;
        const endPos = textarea.selectionEnd;

        setUrl(
            url.substring(0, startPos) +
            variable +
            url.substring(endPos)
        );

        // Focus back on the input and position cursor after inserted variable
        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(startPos + variable.length, startPos + variable.length);
        }, 0);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Modal open={isOpen} onClose={onClose}>
            <ModalBox sx={{
                width: { xs: '95%', sm: '800px', md: '900px' },
                maxHeight: { xs: '95vh', sm: '90vh' },
                p: 3,
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3
            }}>
                <Box flex={2}>
                    <Typography variant="h6" mb={2}>Enter preview URL</Typography>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label="Preview URL"
                            variant="outlined"
                            value={url}
                            onChange={handleChange}
                            error={!!error}
                            helperText={error}
                            placeholder="https://example.com/preview?id={entry.id}"
                            autoFocus
                            multiline
                        />

                        <Box display="flex" justifyContent="flex-end" gap={2}>
                            <Button variant="outlined" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!url.trim()}
                                sx={{ width: '70px' }}
                            >
                                {loading ? (
                                    <CircularProgress size={22} />
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </Box>
                    </Stack>
                </Box>

                <Box flex={1}>
                    <Typography variant="subtitle1" mb={2} display="flex" alignItems="center" gap={1}>
                        <InfoOutlinedIcon fontSize="small" />
                        Available variables
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            {variables.map((varItem) => (
                                <Box key={varItem.name}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                        <Typography variant="body2" fontWeight="bold">
                                            {varItem.name}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                            onClick={() => addVariable(varItem.value)}
                                        >
                                            Insert
                                        </Button>
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {varItem.description}
                                    </Typography>

                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Box>
            </ModalBox>
        </Modal>
    );
};

export default PreviewUrlModal;